import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cx } from "./cx";
import { Spinner } from "./Spinner";
import "./Button.css";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner, keeps the width, sets aria-busy and swallows clicks. */
  loading?: boolean;
  /** Leading icon. */
  icon?: ReactNode;
  /** Trailing icon. */
  iconRight?: ReactNode;
  fullWidth?: boolean;
}

/** Action control. Defaults to `type="button"` so it never submits a form by accident. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "secondary",
    size = "md",
    loading = false,
    icon,
    iconRight,
    fullWidth = false,
    disabled,
    type = "button",
    className,
    children,
    onClick,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx(
        "ui-button",
        `ui-button--${variant}`,
        `ui-button--${size}`,
        loading && "ui-button--loading",
        fullWidth && "ui-button--full",
        className,
      )}
      disabled={disabled}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={loading ? (e) => e.preventDefault() : onClick}
      {...rest}
    >
      <span className="ui-button__content">
        {icon ? <span className="ui-button__icon">{icon}</span> : null}
        {children}
        {iconRight ? <span className="ui-button__icon">{iconRight}</span> : null}
      </span>
      {loading ? (
        <span className="ui-button__spinner">
          <Spinner size="sm" decorative inheritColor />
        </span>
      ) : null}
    </button>
  );
});
